import React, { useEffect } from "react";
import PropTypes from "prop-types";
import {
  Toolbar,
  Typography,
  Tooltip,
  IconButton,
  Chip,
  Box,
  Stack,
} from "@mui/material";
import { FilterList, Clear, ViewList, ViewModule } from "@mui/icons-material";
import { CreateButton } from "components/controls";
import AdvancedFiltersModal from "./AdvancedFiltersModal";
import ExportMenu from "./ExportMenu";
import ColumnManager from "./ColumnManager";

const styles = {
  title: {
    flex: 1,
  },
};

function ToolBar({
  title,
  columns,
  filters,
  sorts,
  setFilter,
  onCreate,
  createPermissions,
  ExtraToolBar,
  disableCreateButton,
  serverSideUrl,
  onExportStart,
  onExportComplete,
  onExportError,
  visibleColumns,
  onColumnVisibilityChange,
  columnWidths,
  onColumnWidthChange,
  onSortChange,
  onRemoveSort,
  onClearAllSorts,
  viewMode,
  onViewModeChange,
  showViewToggle,
}) {
  const [showAdvancedFilters, setShowAdvancedFilters] = React.useState(false);

  // Hotkey: Alt+N para crear desde toolbar (Ctrl+N no es prevenible en navegadores)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!onCreate || disableCreateButton) return;
      if (e.altKey && (e.key === 'n' || e.key === 'N')) {
        e.preventDefault();
        onCreate();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCreate, disableCreateButton]);

  // Función para obtener el texto descriptivo del ordenamiento
  const getSortDescription = (columnName, sortOrder) => {
    const column = columns.find(col => col.name === columnName);
    const columnLabel = column ? column.label : columnName;
    const orderText = sortOrder === 'asc' ? 'ascendente' : 'descendente';
    return `${columnLabel} (${orderText})`;
  };

  // Función para obtener el texto descriptivo del filtro
  const getFilterDescription = (columnName, filterData) => {
    const column = columns.find(col => col.name === columnName);
    const columnLabel = column ? column.label : columnName;

    if (filterData.operator) {
      if (filterData.operator === 'in') {
        // Filtro de lista
        const values = Array.isArray(filterData.value) ? filterData.value : [filterData.value];
        return `${columnLabel} en: ${values.join(', ')}`;
      } else if (['is_null', 'is_not_null'].includes(filterData.operator)) {
        // Filtros de nulos
        const operatorText = {
          'is_null': 'es nulo',
          'is_not_null': 'no es nulo'
        }[filterData.operator];
        return `${columnLabel} ${operatorText}`;
      } else if (filterData.operator === 'equals' && column?.filterType === 'boolean') {
        // Filtro booleano
        const booleanOptions = column.booleanOptions || { true: 'Sí', false: 'No' };
        const displayValue = booleanOptions[filterData.value] || filterData.value;
        return `${columnLabel} es ${displayValue}`;
      } else {
        // Filtro de texto
        const operatorText = {
          'contains': 'contiene',
          'equals': 'igual a',
          'starts_with': 'comienza por',
          'ends_with': 'termina por',
          'not_contains': 'no contiene',
          'greater_than': 'mayor que',
          'less_than': 'menor que',
          'greater_equal': 'mayor o igual que',
          'less_equal': 'menor o igual que'
        }[filterData.operator] || filterData.operator;

        return `${columnLabel} ${operatorText} "${filterData.value}"`;
      }
    } else if (filterData.from || filterData.to) {
      // Filtro de rango
      const filterType = column?.filterType || 'number';

      if (filterType === 'date') {
        // Filtro de fecha
        if (filterData.from && filterData.to) {
          return `${columnLabel}: ${filterData.from} - ${filterData.to}`;
        } else if (filterData.from) {
          return `${columnLabel}: desde ${filterData.from}`;
        } else if (filterData.to) {
          return `${columnLabel}: hasta ${filterData.to}`;
        }
      } else if (filterType === 'time') {
        // Filtro de hora
        if (filterData.from && filterData.to) {
          return `${columnLabel}: ${filterData.from} - ${filterData.to}`;
        } else if (filterData.from) {
          return `${columnLabel}: desde ${filterData.from}`;
        } else if (filterData.to) {
          return `${columnLabel}: hasta ${filterData.to}`;
        }
      } else if (filterType === 'datetime') {
        // Filtro de fecha y hora
        const formatDateTime = (dateTimeString) => {
          // Si es formato local (YYYY-MM-DD HH:MM:SS)
          if (typeof dateTimeString === 'string' && dateTimeString.includes(' ')) {
            const [datePart, timePart] = dateTimeString.split(' ');
            const [year, month, day] = datePart.split('-');
            const [hours, minutes] = timePart.split(':');
            return `${day}/${month}/${year} ${hours}:${minutes}`;
          }

          // Si es ISO string
          const date = new Date(dateTimeString);
          return date.toLocaleString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });
        };

        if (filterData.from && filterData.to) {
          return `${columnLabel}: ${formatDateTime(filterData.from)} - ${formatDateTime(filterData.to)}`;
        } else if (filterData.from) {
          return `${columnLabel}: desde ${formatDateTime(filterData.from)}`;
        } else if (filterData.to) {
          return `${columnLabel}: hasta ${formatDateTime(filterData.to)}`;
        }
      } else {
        // Filtro numérico
        if (filterData.from && filterData.to) {
          return `${columnLabel}: ${filterData.from} - ${filterData.to}`;
        } else if (filterData.from) {
          return `${columnLabel}: desde ${filterData.from}`;
        } else if (filterData.to) {
          return `${columnLabel}: hasta ${filterData.to}`;
        }
      }
    }

    return `${columnLabel}: ${JSON.stringify(filterData)}`;
  };

  // Función para limpiar un filtro específico
  const clearSpecificFilter = (columnName) => {
    const newFilters = { ...filters };
    delete newFilters[columnName];
    setFilter(newFilters);
  };

  // Función para limpiar un ordenamiento específico
  const clearSpecificSort = (columnName) => {
    if (onRemoveSort) {
      onRemoveSort(columnName);
    }
  };

  // Función para limpiar todos los ordenamientos
  const clearAllSorts = () => {
    if (onClearAllSorts) {
      onClearAllSorts();
    }
  };

  // Función para limpiar todos los filtros
  const clearAllFilters = () => {
    setFilter({});
  };

  // Función para aplicar filtros avanzados
  const handleApplyAdvancedFilters = (newFilters) => {
    // Combinar filtros existentes con los nuevos filtros avanzados
    const combinedFilters = { ...filters, ...newFilters };
    setFilter(combinedFilters);
  };

  // Obtener filtros activos
  const activeFilters = Object.keys(filters || {}).filter(key => filters[key]);

  // Obtener ordenamientos activos
  const activeSorts = Object.keys(sorts || {}).filter(key => sorts[key]);

  return (
    <>
      <Toolbar sx={{ minHeight: "48px !important", py: 0.5 }}>
        <Typography
          style={styles.title}
          variant="h6"
          id="tableTitle"
          component="div"
          sx={{ fontSize: "1rem", fontWeight: 600 }}
        >
          {title || ""}
        </Typography>

        {/* Mostrar filtros activos */}
        {activeFilters.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
              Filtros:
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.25 }}>
              {activeFilters.map(columnName => (
                <Chip
                  key={columnName}
                  label={getFilterDescription(columnName, filters[columnName])}
                  size="small"
                  onDelete={() => clearSpecificFilter(columnName)}
                  color="primary"
                  variant="outlined"
                  sx={{
                    height: "20px",
                    fontSize: "0.7rem",
                    "& .MuiChip-label": {
                      px: 0.5,
                      fontSize: "0.7rem",
                    },
                    "& .MuiChip-deleteIcon": {
                      fontSize: "0.8rem",
                    },
                  }}
                />
              ))}
            </Stack>
            <Tooltip title="Limpiar todos los filtros">
              <IconButton
                size="small"
                onClick={clearAllFilters}
                sx={{ ml: 0.5, p: 0.25 }}
              >
                <Clear fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Mostrar ordenamientos activos */}
        {activeSorts.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
              Ordenamiento:
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.25 }}>
              {activeSorts.map(columnName => (
                <Chip
                  key={columnName}
                  label={getSortDescription(columnName, sorts[columnName])}
                  size="small"
                  onDelete={() => clearSpecificSort(columnName)}
                  color="secondary"
                  variant="outlined"
                  sx={{
                    height: "20px",
                    fontSize: "0.7rem",
                    "& .MuiChip-label": {
                      px: 0.5,
                      fontSize: "0.7rem",
                    },
                    "& .MuiChip-deleteIcon": {
                      fontSize: "0.8rem",
                    },
                  }}
                />
              ))}
            </Stack>
            <Tooltip title="Limpiar todos los ordenamientos">
              <IconButton
                size="small"
                onClick={clearAllSorts}
                sx={{ ml: 0.5, p: 0.25 }}
              >
                <Clear fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <Tooltip title="Filtros avanzados">
          <IconButton
            onClick={() => setShowAdvancedFilters(true)}
            size="small"
            sx={{
              p: 0.5,
              "&:hover": (theme) => ({
                color: theme.palette.primary.main,
              }),
            }}
          >
            <FilterList fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Menú de exportación */}
        {serverSideUrl && (
          <>
            <ExportMenu
              serverSideUrl={serverSideUrl}
              filters={filters}
              sorts={sorts}
              columns={columns}
              title={title}
              onExportStart={onExportStart}
              onExportComplete={onExportComplete}
              onExportError={onExportError}
              visibleColumns={visibleColumns}
            />
          </>
        )}

        {/* Gestor de columnas */}
        <ColumnManager
          columns={columns}
          visibleColumns={visibleColumns}
          onColumnVisibilityChange={onColumnVisibilityChange}
        />

        {/* Botón de cambio de vista */}
        {showViewToggle && (
          <Tooltip title={viewMode === 'list' ? 'Vista catálogo' : 'Vista lista'}>
            <IconButton
              onClick={() => onViewModeChange(viewMode === 'list' ? 'catalog' : 'list')}
              size="small"
              sx={{
                p: 0.5,
                "&:hover": (theme) => ({
                  color: theme.palette.primary.main,
                }),
              }}
            >
              {viewMode === 'list' ? (
                <ViewModule fontSize="small" />
              ) : (
                <ViewList fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}

        {onCreate && (
          <Tooltip title="Adicionar (Alt+N)">
            <span>
              <CreateButton
                onClick={onCreate}
                requiredPermissions={createPermissions}
                disabled={disableCreateButton}
              />
            </span>
          </Tooltip>
        )}
        {ExtraToolBar}
      </Toolbar>

      {/* Modal de filtros avanzados */}
      <AdvancedFiltersModal
        open={showAdvancedFilters}
        onClose={() => setShowAdvancedFilters(false)}
        columns={columns}
        currentFilters={filters}
        onApplyFilters={handleApplyAdvancedFilters}
      />
    </>
  );
}

ToolBar.propTypes = {
  title: PropTypes.string,
  ExtraToolBar: PropTypes.node,
  columns: PropTypes.oneOfType([PropTypes.array]),
  filters: PropTypes.oneOfType([PropTypes.object]),
  sorts: PropTypes.oneOfType([PropTypes.object]),
  setFilter: PropTypes.func,
  onCreate: PropTypes.func,
  disableCreateButton: PropTypes.bool,
  createPermissions: PropTypes.string,
  serverSideUrl: PropTypes.string,
  onExportStart: PropTypes.func,
  onExportComplete: PropTypes.func,
  onExportError: PropTypes.func,
  visibleColumns: PropTypes.arrayOf(PropTypes.string),
  onColumnVisibilityChange: PropTypes.func,
  columnWidths: PropTypes.object,
  onColumnWidthChange: PropTypes.func,
  onSortChange: PropTypes.func,
  onRemoveSort: PropTypes.func,
  onClearAllSorts: PropTypes.func,
  viewMode: PropTypes.oneOf(['list', 'catalog']),
  onViewModeChange: PropTypes.func,
  showViewToggle: PropTypes.bool,
};

export default ToolBar;